import classnames from 'classnames';
import * as React from 'react';
import { FitmentSelectorWrapper } from 'sunhammer-ui';
import {
  FitmentSelectorProps,
  Item,
  SelectedValues,
} from 'sunhammer-ui/build/typings/components/FitmentSelector/models';
import { FitmentSelectorWrapperProps } from 'sunhammer-ui/build/typings/components/FitmentSelectorWrapper/models';
import { SelectedFitmentValues } from '.';
import Modal from '../../common/Modal/Modal';
import styles from './fitmentSelectorModal.module.scss';

interface FitmentSelectorModalProps {
  classes: { root: string };
  isModalOpen: boolean;
  disbaleAllHtmlEl: boolean;
  showModal: (value: boolean) => void;
  onSubmit?: (values: SelectedFitmentValues, changed: boolean) => void;
  groupId?: FitmentSelectorWrapperProps['groupId'];
  /**
   * Pipe delimited Fitment
   * Example: 2020|Chevrolet|Colorado
   */
  selectedFitment?: string;
}

const FitmentSelectorModal = ({
  classes,
  disbaleAllHtmlEl,
  isModalOpen,
  showModal,
  onSubmit,
  groupId,
  selectedFitment: initialSelectedFitment,
}: FitmentSelectorModalProps) => {
  const [fitmentLabels, setFitmentLabels] = React.useState<Item[]>([]);
  const [fitmentValues, setFitmentValues] =
    React.useState<FitmentSelectorProps['labelsData']>();
  const [selectedFitment, setSelectedFitment] =
    React.useState<SelectedValues>();
  const [fitmentKey, setFitmentKey] = React.useState('');
  const [isChanged, setIsChanged] = React.useState(false);

  React.useEffect(() => {
    if (fitmentLabels.length && initialSelectedFitment) {
      const selectedFitmentValues = initialSelectedFitment.split('|');
      const selectedFitmentParsed = fitmentLabels.reduce(
        (acc, label, index) => ({
          ...acc,
          [label.name]: selectedFitmentValues[index],
        }),
        {}
      );
      setSelectedFitment(selectedFitmentParsed);
      setFitmentKey(JSON.stringify(selectedFitmentParsed));
    }
  }, [fitmentLabels, initialSelectedFitment]);

  React.useEffect(() => {
    setIsChanged(false);
  }, [isModalOpen]);

  if (!groupId) {
    return null;
  }

  return (
    <Modal
      className={classnames(classes.root, styles.modal, {
        disableAll: disbaleAllHtmlEl,
      })}
      isOpen={isModalOpen}
      onClose={() => showModal(false)}
      showCloseButton={true}
    >
      <div className={styles.header}>
        <h1 className="Pl-Fitment-modal--title">Select your fitment</h1>
      </div>

      <div className={styles.modalBody}>
        <FitmentSelectorWrapper
          /* 
            Need to change the key when fitment labels
            or selected fitment changes to set initial values properly.
            This will re-mount the component
          */
          key={fitmentKey}
          onDataLoaded={(labels, _, data) => {
            if (labels && data) {
              setFitmentLabels(labels);
              setFitmentValues(data);
            }
          }}
          className={styles.firmentSelector}
          styled={false}
          orientation="vertical"
          searchButtonText="Save"
          onSubmit={(values) => {
            onSubmit?.(
              convertSelectedDataToArray(
                fitmentLabels,
                values,
                fitmentValues || {}
              ),
              isChanged
            );
            showModal(false);
          }}
          onChange={(_, values) => {
            setIsChanged(true);
            if (!values) {
              onSubmit?.([], true);
            }
          }}
          groupId={groupId}
          selectedValues={selectedFitment}
        />
      </div>
    </Modal>
  );
};

export default FitmentSelectorModal;

function convertSelectedDataToArray(
  labels: Item[],
  values: SelectedValues,
  labelValues: FitmentSelectorProps['labelsData']
) {
  const parsedValues = [];
  for (const label of labels) {
    if (values[label.name] && labelValues[label.name]) {
      const selectedValueName = labelValues[label.name].find(
        (item) => item.id === values[label.name]
      )?.name;

      parsedValues.push({
        key: label.name,
        value: selectedValueName as string | number,
      });
    }
  }

  return parsedValues;
}
